'use client';

import React, { useMemo } from 'react';
import { Modal } from '@/components/ui/Modal';
import { IconCrane, IconScrewdriverWrench } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { SectionHeader } from '@/components/ui';
import { toTitleCase } from '@/utils/textTransform';

interface ConstructionProject {
  house?: string;
  streetName?: string;
  expirationDate?: string;
  jobType?: string;
  jobStartDate?: string;
  permitStatus?: string;
  filingStatus?: string;
  borough?: string;
  zipCode?: string;
  // Support snake_case from API
  house__?: string;
  street_name?: string;
  expiration_date?: string;
  job_type?: string;
  job_start_date?: string;
  permit_status?: string;
  filing_status?: string;
  zip_code?: string;
}

interface ConstructionHistoryContentProps {
  constructionData: Record<string, ConstructionProject[]>;
  isMajor: boolean;
}

// Format date to "MMM DD, YYYY" format
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    // Handle MM/DD/YYYY format
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        const monthNum = parseInt(month || '0', 10);
        const dayNum = parseInt(day || '0', 10);
        const yearNum = parseInt(year || '0', 10);
        if (monthNum && dayNum && yearNum) {
          const date = new Date(yearNum, monthNum - 1, dayNum);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        }
      }
    }
    // Handle ISO format
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return dateString;
  } catch {
    return dateString;
  }
};

// Format address from house and street and convert to readable casing
const formatAddress = (project: ConstructionProject): string => {
  const house = project.house || project.house__ || '';
  const street = project.streetName || project.street_name || '';
  const raw = [house, street].filter(Boolean).join(' ').trim();
  return raw ? toTitleCase(raw) : 'Address not available';
};

// Permit type descriptions
const PERMIT_DESCRIPTIONS: Record<string, string> = {
  a1: 'For major renovations that affect space usage, such as converting commercial space into residential or extending floor levels.',
  nb: 'For construction of a new building or structure.',
  dm: 'For full demolition, which means dismantling, razing, or removing all of a building or structure.',
  a2: 'For multiple alterations that do not affect space usage, accessibility, or occupancy.',
  a3: 'For a single alteration that does not affect space usage, accessibility, or occupancy.',
};

// Transform API response to camelCase
const transformProject = (project: any): ConstructionProject => {
  return {
    house: project.house ?? project.house__ ?? undefined,
    streetName: project.streetName ?? project.street_name ?? undefined,
    expirationDate: project.expirationDate ?? project.expiration_date ?? undefined,
    jobType: project.jobType ?? project.job_type ?? undefined,
    jobStartDate: project.jobStartDate ?? project.job_start_date ?? undefined,
    permitStatus: project.permitStatus ?? project.permit_status ?? undefined,
    filingStatus: project.filingStatus ?? project.filing_status ?? undefined,
    borough: project.borough,
    zipCode: project.zipCode ?? project.zip_code ?? undefined,
  };
};

// Content component for use with ModalTrigger
export function ConstructionHistoryContent({
  constructionData,
  isMajor,
}: ConstructionHistoryContentProps) {
  // Transform construction data to camelCase
  const transformedData = useMemo(() => {
    const transformed: Record<string, ConstructionProject[]> = {};
    for (const permitType in constructionData) {
      const projects = constructionData[permitType];
      if (Array.isArray(projects)) {
        transformed[permitType] = projects.map(transformProject);
      }
    }
    return transformed;
  }, [constructionData]);

  const permitTypes = Object.keys(transformedData || {}).filter(key => {
    const projects = transformedData[key];
    return Array.isArray(projects) && projects.length > 0;
  });

  if (permitTypes.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-body-2 text-secondary">No construction data available.</p>
      </div>
    );
  }

  const Icon = isMajor ? IconCrane : IconScrewdriverWrench;
  const iconColor = isMajor ? 'text-caution-mid' : 'text-caution';

  return (
    <div className="space-y-2">
      {permitTypes.map((permitType, permitIndex) => {
        const projects = transformedData[permitType] || [];
        const description = PERMIT_DESCRIPTIONS[permitType] || '';

        return (
          <div key={permitType}>
            {/* Permit Type Section */}
            <div>
              <SectionHeader
                titleContainerClassName="!px-2"
                subtitleContainerClassName="!px-2"
                title={`Projects with ${permitType.toUpperCase()} permit`}
                subtitleText={description}
              />

              {/* Projects List */}
              <div>
                {projects.map((project, projectIndex) => {
                  const address = formatAddress(project);
                  const expirationDate = formatDate(project.expirationDate);

                  return (
                    <div key={projectIndex}>
                      <div className="flex items-center gap-4 px-2 py-3 rounded-lg hover:bg-secondary transition-colors">
                        <Icon className={cn('w-5 h-5 shrink-0 mt-0.5', iconColor)} />
                        <div className="flex-1 min-w-0">
                          <div className="text-body-2 text-primary mb-1">{address}</div>
                          {expirationDate && (
                            <div className="text-caption text-secondary">
                              Permit expires {expirationDate}
                            </div>
                          )}
                        </div>
                      </div>
                      {projectIndex < projects.length - 1 && (
                        <div className="border-b border-tertiary my-1 mx-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Wrapper component for standalone use (if needed)
interface ConstructionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  constructionData: Record<string, ConstructionProject[]>;
  isMajor: boolean;
}

export function ConstructionHistoryModal({
  open,
  onClose,
  constructionData,
  isMajor,
}: ConstructionHistoryModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      label={isMajor ? 'Major construction' : 'Minor construction'}
    >
      <ConstructionHistoryContent constructionData={constructionData} isMajor={isMajor} />
    </Modal>
  );
}
